import { useRef } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Upload } from '@mint-lib/icons';
import { Illustration } from '@mint-lib/illustrations';
import { Button, typographyVariants } from '@mint-lib/tw-components';
import {
  FileUploader,
  type BaseFile,
  type FileUploaderV2RefType,
} from '@mint-lib/tw-components/patterns';

import {
  FILE_FORMATS,
  MAX_FILES,
  MAX_SIZE_BYTES,
} from '../../constants/uploadingFiles.js';
import { rejectDuplicateName } from '../../helpers/knowledge-base/uploadValidation.js';

const MAX_SIZE_MB = MAX_SIZE_BYTES / 1024 / 1024;

type Props = {
  existingFileNames: string[];
  onFilesPicked: (files: BaseFile[]) => void;
};

const noUpload = () => Promise.resolve();

const EmptyFolderDropzone = ({ existingFileNames, onFilesPicked }: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const uploaderRef = useRef<FileUploaderV2RefType | null>(null);

  const handleChange = (files: BaseFile[]) => {
    if (!files.length) return;

    onFilesPicked(files);
    uploaderRef.current?.setFiles([]);
  };

  return (
    <div className="flex h-full flex-col items-center justify-center">
      <FileUploader
        ref={uploaderRef}
        accept={FILE_FORMATS}
        maxFiles={MAX_FILES}
        maxSize={MAX_SIZE_BYTES}
        validate={rejectDuplicateName(
          existingFileNames,
          t('File name already exists'),
        )}
        onChange={handleChange}
        onUpload={noUpload}
        renderItem={() => null}
        noClick
        noKeyboard
      >
        {({ getRootProps, getInputProps, isDragActive, open }) => (
          <div
            data-testid="empty-folder-dropzone"
            data-state={isDragActive ? 'active' : 'inactive'}
            className="flex w-115.5 flex-col items-center gap-3 rounded-sm border border-dashed border-border-secondary bg-ui-primary px-6 py-10 data-[state=active]:border-tertiary-main data-[state=active]:bg-background-tertiary"
            {...getRootProps()}
          >
            <input {...getInputProps()} />

            <Illustration
              className="size-14"
              component="IllustrationFileUpload"
            />

            <div className="flex flex-row flex-wrap items-center justify-center gap-1">
              <p className={typographyVariants({ variant: 'bodyShort02' })}>
                {t('Drag & drop to upload files or')}
              </p>
              <Button
                type="button"
                variant="text"
                data-dd-action-name="Knowledge base: browse files in empty folder"
                onClick={open}
              >
                <Upload />
                {t('Browse files')}
              </Button>
            </div>
          </div>
        )}
      </FileUploader>
    </div>
  );
};

export { EmptyFolderDropzone };
